/**
 * The Fibonacci module: retracements drawn on swings.
 *
 * This is the strategy's main module, and the one every other signal is
 * weighed against (see MODULE_IMPORTANCE). It works on exactly three
 * timeframes rather than offering all of them: the swings worth drawing on
 * live on M1, M15, H1, H4 and D1, and letting the module answer for a timeframe the
 * strategy does not use would present noise as a choice.
 *
 * Swings are NOT detected here yet. The rules for choosing one are being
 * taught, and until they are settled the swing on each chart is drawn by hand
 * (see apps/web/public/fib/manual.json). This file holds only what a drawn
 * swing needs: its shape, and the arithmetic of the levels on it.
 */
export const FIB_TIMEFRAMES = ['M1', 'M15', 'H1', 'H4', 'D1'];
/**
 * How many closed bars the module loads per timeframe.
 *
 * Enough history for a swing to be found well behind the current price, but
 * bounded: a retracement is measured against recent structure, and D1 at 600
 * bars is already well over two years of sessions.
 */
export const FIB_SCAN_BARS = {
    // Deep on purpose: the owner draws teaching examples on history, so the
    // chart has to reach well back. Roughly: M1 three weeks, M15 nine months,
    // H1 three years, H4 six years, D1 everything there is.
    M1: 20000,
    M15: 15000,
    H1: 15000,
    H4: 8000,
    D1: 5000,
};
/** Bars visible when a chart opens, so the bodies stay readable. */
export const FIB_VISIBLE_BARS = {
    M1: 240,
    M15: 220,
    H1: 220,
    H4: 200,
    D1: 160,
};
/**
 * The retracement ratios drawn on a swing.
 *
 * The standard set, as a starting point. 0 sits at the swing's end and 1 at
 * its start, so a retracement is read as how far back toward the start price
 * has come. Which of these the strategy actually trades is a rule for later;
 * the chart draws all of them until then.
 *
 * 0.118, 0.441, 0.559 and 0.702 are the owner's additions: the midpoints
 * of 0–0.236, 0.382–0.5, 0.5–0.618 and 0.618–0.786.
 */
export const FIB_RATIOS = [0, 0.118, 0.236, 0.382, 0.441, 0.5, 0.559, 0.618, 0.702, 0.786, 1];
export function swingRange(swing) {
    return Math.abs(swing.end.price - swing.start.price);
}
/**
 * Retracement levels for a swing.
 *
 * Ratio 0 is the swing's end and 1 its start, so on an up swing the levels
 * descend from the high and on a down swing they rise from the low — the way
 * every charting tool draws the retracement.
 */
export function fibLevels(swing, ratios = FIB_RATIOS) {
    const sign = swing.direction === 'up' ? -1 : 1;
    const range = swingRange(swing);
    return ratios.map((ratio) => ({ ratio, price: swing.end.price + sign * ratio * range }));
}
/**
 * How far `price` has retraced the swing, as a ratio of its range.
 *
 * 0 at the swing's end, 1 back at its start. Negative means price has gone
 * beyond the end (the swing is extending, not retracing); above 1 means the
 * start has been taken out and the swing is broken.
 */
export function retracementOf(swing, price) {
    const range = swingRange(swing);
    if (range <= 0)
        return 0;
    return swing.direction === 'up'
        ? (swing.end.price - price) / range
        : (price - swing.end.price) / range;
}
export function swingInvalidation(swing, bars) {
    const up = swing.direction === 'up';
    const startEdge = swing.start.extreme ?? swing.start.price;
    const endEdge = swing.end.extreme ?? swing.end.price;
    for (const bar of bars) {
        if (bar.time <= swing.end.time)
            continue;
        // Past the end: a new extreme in the swing's own direction.
        if (up ? bar.high > endEdge : bar.low < endEdge) {
            return { passed: 'end', time: bar.time, price: up ? bar.high : bar.low };
        }
        // Past the start: the retracement went the whole way and beyond.
        if (up ? bar.low < startEdge : bar.high > startEdge) {
            return { passed: 'start', time: bar.time, price: up ? bar.low : bar.high };
        }
    }
    return null;
}
/**
 * What counts as a reversal on each timeframe, as a share of price.
 *
 * The one rule that is still a number rather than a definition. A
 * counter-move smaller than this is a pullback inside the move; larger, and
 * the move has ended. Tuned by hand against the owner's draws so far and
 * meant to keep being tuned against them: percent of price rather than a
 * fixed amount so the same setting holds as gold moves from 2000 to 5000.
 */
export const FIB_REVERSAL_PCT = {
    M1: 0.002,
    M15: 0.005,
    // 1.3%: the 26–27 Oct pullback inside the 23→29 Oct rise was 1.0%, and the
    // owner reads that rise as one move.
    H1: 0.013,
    H4: 0.013,
    D1: 0.026,
};
/**
 * ZigZag pivots: alternating highs and lows, each confirmed once price has
 * moved away from it by at least `pct` of its price.
 *
 * The leg in progress is NOT emitted here. Its extreme is unconfirmed by
 * definition; `latestLeg` reports it separately as a running end.
 */
export function detectPivots(bars, pct) {
    const out = [];
    if (bars.length === 0 || pct <= 0)
        return out;
    let direction = null;
    let hiIndex = 0;
    let hiPrice = bars[0].high;
    let loIndex = 0;
    let loPrice = bars[0].low;
    for (let i = 1; i < bars.length; i++) {
        const bar = bars[i];
        if (direction !== 'down') {
            if (bar.high > hiPrice) {
                hiPrice = bar.high;
                hiIndex = i;
            }
            if (hiPrice - bar.low >= hiPrice * pct) {
                out.push({ time: bars[hiIndex].time, price: hiPrice, kind: 'high', confirmedTime: bar.time });
                direction = 'down';
                loPrice = bar.low;
                loIndex = i;
                continue;
            }
        }
        if (direction !== 'up') {
            if (bar.low < loPrice) {
                loPrice = bar.low;
                loIndex = i;
            }
            if (bar.high - loPrice >= loPrice * pct) {
                out.push({ time: bars[loIndex].time, price: loPrice, kind: 'low', confirmedTime: bar.time });
                direction = 'up';
                hiPrice = bar.high;
                hiIndex = i;
            }
        }
    }
    return out;
}
/**
 * The swing the rules point at right now.
 *
 * Normally the last completed leg: from the second-last confirmed pivot to
 * the last one (rule 4 — end = the last reversal, start = where the move into
 * it began). If price has since passed that leg's start, the leg is gone
 * (rule 5) and what is left is the move in progress: from the last pivot to
 * the extreme reached since, which keeps moving until a reversal confirms.
 */
export function latestLeg(bars, pct) {
    const pivots = detectPivots(bars, pct);
    const n = pivots.length;
    if (n === 0)
        return null;
    const last = pivots[n - 1];
    const runningLeg = () => {
        // From the last pivot to the extreme since it.
        const up = last.kind === 'low';
        let best = null;
        for (const bar of bars) {
            if (bar.time <= last.time)
                continue;
            if (best === null || (up ? bar.high > best.high : bar.low < best.low))
                best = bar;
        }
        const end = best
            ? { time: best.time, price: up ? best.high : best.low }
            : { time: last.time, price: last.price };
        return {
            swing: { direction: up ? 'up' : 'down', start: { time: last.time, price: last.price }, end },
            running: true,
            pivots,
        };
    };
    if (n < 2)
        return runningLeg();
    const prev = pivots[n - 2];
    const swing = {
        direction: last.kind === 'high' ? 'up' : 'down',
        start: { time: prev.time, price: prev.price },
        end: { time: last.time, price: last.price },
    };
    // Passed its start since? Then the retracement became the next move.
    const gone = swingInvalidation(swing, bars);
    if (gone?.passed === 'start')
        return runningLeg();
    return { swing, running: false, pivots };
}
/**
 * The confirmation point of an extreme, on finer bars (owner's rule 13/14).
 *
 * For a top: after the extreme, take the first pullback low — the low price
 * held before the first bounce (a bar whose high beats the high of the bar
 * that made the low). The reversal is confirmed by the first bar that CLOSES
 * below that pullback low. The point is the highest high of the pullback
 * that follows, before a bar closes below the confirmation bar's low; if
 * price runs at once, the confirmation bar's own high. Mirror for a bottom.
 *
 * `fine` is the finer bars covering the extreme's own bar and some way after
 * it. Returns null when the structure has not completed inside `fine`.
 */
export function refineExtreme(fine, kind, extremeTime, extremeSpan) {
    // The finer bar that made the extreme, inside the coarse bar's span.
    let i0 = -1;
    for (let i = 0; i < fine.length; i++) {
        const b = fine[i];
        if (b.time < extremeTime || b.time >= extremeTime + extremeSpan)
            continue;
        if (i0 === -1 || (kind === 'high' ? b.high > fine[i0].high : b.low < fine[i0].low))
            i0 = i;
    }
    if (i0 === -1)
        return null;
    const extreme = kind === 'high' ? fine[i0].high : fine[i0].low;
    const top = kind === 'high';
    // First pullback: the extreme of the dip/bounce before the first counter-bar.
    let pIdx = -1;
    let pullback = 0;
    let bounceIdx = -1;
    for (let j = i0 + 1; j < fine.length; j++) {
        const b = fine[j];
        if (pIdx === -1 || (top ? b.low < pullback : b.high > pullback)) {
            // Still extending the first dip — unless this bar is the bounce.
            if (pIdx !== -1 && (top ? b.high > fine[pIdx].high : b.low < fine[pIdx].low)) {
                bounceIdx = j;
                break;
            }
            pIdx = j;
            pullback = top ? b.low : b.high;
            continue;
        }
        if (top ? b.high > fine[pIdx].high : b.low < fine[pIdx].low) {
            bounceIdx = j;
            break;
        }
    }
    if (bounceIdx === -1)
        return null;
    // Confirmation: first close through the pullback.
    let cIdx = -1;
    for (let k = bounceIdx; k < fine.length; k++) {
        const b = fine[k];
        if (top ? b.close < pullback : b.close > pullback) {
            cIdx = k;
            break;
        }
    }
    if (cIdx === -1)
        return null;
    const conf = fine[cIdx];
    // The pullback after the confirmation bar, until price closes beyond it.
    let best = null;
    for (let m = cIdx + 1; m < fine.length; m++) {
        const b = fine[m];
        if (top ? b.close < conf.low : b.close > conf.high)
            break;
        if (best === null || (top ? b.high > best.high : b.low < best.low))
            best = b;
    }
    const point = best ?? conf;
    return {
        time: point.time,
        price: top ? point.high : point.low,
        extreme,
        confirmedTime: conf.time,
    };
}
//# sourceMappingURL=fibonacci.js.map